/**
 * Element Zero - Main Game Loop
 * Handles UI updates, user interactions, and game loop
 */

import { GameState, ENERGY_BOOST_CONFIG, EnergyBoostLevel } from './core/GameState';
import { formatNumber } from './utils/formatting';

class Game {
  private state: GameState;
  private lastFrameTime: number = 0;
  private animationFrameId: number = 0;
  private eventLog: Array<{ time: string; message: string }> = [];
  private readonly MAX_LOG_ENTRIES = 50;
  
  // Smooth number animation
  private displayedValues: Map<string, number> = new Map();
  private readonly ANIMATION_SPEED = 0.15; // Lerp factor
  
  // Current visual stage
  private currentVisualStage: string = 'subatomic';
  
  // Milestone tracking
  private achievedMilestones: Set<string> = new Set();
  
  // Universe end overlay
  private universeEndOverlay: HTMLElement | null = null;
  
  // Click area visual feedback
  private clickSection: HTMLElement;
  private clickerParticles: HTMLElement;
  private statusStage: HTMLElement;
  private statusInfo: HTMLElement;
  private particleCount: number = 0;

  // UI Elements
  private elements = {
    // Resources
    quarksDisplay: document.getElementById('quarks-display')!,
    protonsDisplay: document.getElementById('protons-display')!,
    neutronsDisplay: document.getElementById('neutrons-display')!,
    energyDisplay: document.getElementById('energy-display')!,
    neutronStarDisplay: document.getElementById('neutron-star-display')!,
    
    // Era Title
    eraTitle: document.getElementById('era-title')!,
    
    // Click area
    quarkClicker: document.getElementById('quark-clicker')!,
    clickPower: document.getElementById('click-power')!,
    comboIndicator: document.getElementById('combo-indicator')!,
    comboProgress: document.getElementById('combo-progress')!,
    
    // Hard Reset
    hardResetButton: document.getElementById('hard-reset-button')!,
    hardResetModal: document.getElementById('hard-reset-modal')!,
    confirmHardReset: document.getElementById('confirm-hard-reset')!,
    cancelHardReset: document.getElementById('cancel-hard-reset')!,
    
    // Quantum Fluctuation
    quantumLevel: document.getElementById('quantum-level')!,
    quantumProduction: document.getElementById('quantum-production')!,
    quantumCost: document.getElementById('quantum-cost')!,
    quantumBuy: document.getElementById('quantum-buy')!,
    quantumCard: document.getElementById('quantum-fluctuation-card')!,
    
    // Strong Interaction
    strongLevel: document.getElementById('strong-level')!,
    strongCost: document.getElementById('strong-cost')!,
    strongBuy: document.getElementById('strong-buy')!,
    strongCard: document.getElementById('strong-interaction-card')!,
    
    // Proton Synthesizer
    protonLevel: document.getElementById('proton-level')!,
    protonCost: document.getElementById('proton-cost')!,
    protonBuy: document.getElementById('proton-buy')!,
    protonCard: document.getElementById('proton-card')!,

    // Neutron Generator
    neutronLevel: document.getElementById('neutron-level')!,
    neutronProduction: document.getElementById('neutron-production')!,
    neutronCost: document.getElementById('neutron-cost')!,
    neutronBuy: document.getElementById('neutron-buy')!,
    neutronCard: document.getElementById('neutron-card')!,

    // Elements
    hydrogenLevel: document.getElementById('hydrogen-level')!,
    hydrogenCost: document.getElementById('hydrogen-cost')!,
    hydrogenBuy: document.getElementById('hydrogen-buy')!,
    hydrogenCard: document.getElementById('hydrogen-card')!,

    heliumLevel: document.getElementById('helium-level')!,
    heliumCost: document.getElementById('helium-cost')!,
    heliumBuy: document.getElementById('helium-buy')!,
    heliumCard: document.getElementById('helium-card')!,

    carbonLevel: document.getElementById('carbon-level')!,
    carbonCost: document.getElementById('carbon-cost')!,
    carbonBuy: document.getElementById('carbon-buy')!,
    carbonCard: document.getElementById('carbon-card')!,

    nitrogenLevel: document.getElementById('nitrogen-level')!,
    nitrogenCost: document.getElementById('nitrogen-cost')!,
    nitrogenBuy: document.getElementById('nitrogen-buy')!,
    nitrogenCard: document.getElementById('nitrogen-card')!,

    oxygenLevel: document.getElementById('oxygen-level')!,
    oxygenCost: document.getElementById('oxygen-cost')!,
    oxygenBuy: document.getElementById('oxygen-buy')!,
    oxygenCard: document.getElementById('oxygen-card')!,

    ironLevel: document.getElementById('iron-level')!,
    ironCost: document.getElementById('iron-cost')!,
    ironBuy: document.getElementById('iron-buy')!,
    ironCard: document.getElementById('iron-card')!,


    // Permanent Upgrades
    permanentSection: document.getElementById('permanent-section')!,
    
    permClickLevel: document.getElementById('perm-click-level')!,
    permClickCost: document.getElementById('perm-click-cost')!,
    permClickBuy: document.getElementById('perm-click-buy')!,

    permProductionLevel: document.getElementById('perm-production-level')!,
    permProductionCost: document.getElementById('perm-production-cost')!,
    permProductionBuy: document.getElementById('perm-production-buy')!,

    permCostLevel: document.getElementById('perm-cost-level')!,
    permCostCost: document.getElementById('perm-cost-cost')!,
    permCostBuy: document.getElementById('perm-cost-buy')!,

    permAutoLevel: document.getElementById('perm-auto-level')!,
    permAutoCost: document.getElementById('perm-auto-cost')!,
    permAutoBuy: document.getElementById('perm-auto-buy')!,

    permStartingLevel: document.getElementById('perm-starting-level')!,
    permStartingCost: document.getElementById('perm-starting-cost')!,
    permStartingBuy: document.getElementById('perm-starting-buy')!,
    
    // Stats
    universeCycle: document.getElementById('universe-cycle')!,
    totalQuarks: document.getElementById('total-quarks')!,
    quarksPerSec: document.getElementById('quarks-per-sec')!,
    energyPerSec: document.getElementById('energy-per-sec')!,
    
    // Event log
    eventLog: document.getElementById('event-log')!,

    // Energy Accelerator (now in middle column)
    energyAcceleratorSection: document.getElementById('energy-accelerator-container')!,
    acceleratorStatus: document.getElementById('accelerator-status')!,
    acceleratorMultiplier: document.getElementById('accelerator-multiplier')!,
    acceleratorDrain: document.getElementById('accelerator-drain')!,
    boostButtons: {
      0: document.getElementById('boost-off')!,
      1: document.getElementById('boost-1')!,
      2: document.getElementById('boost-2')!,
      3: document.getElementById('boost-3')!,
      4: document.getElementById('boost-4')!,
    },
    energyResource: document.querySelector('.energy-resource')!,
  };

  constructor() {
    this.state = new GameState();
    
    // Initialize click area feedback
    this.clickSection = document.querySelector('.click-section')!;
    this.clickerParticles = document.getElementById('clicker-particles')!;
    this.statusStage = document.getElementById('status-stage')!;
    this.statusInfo = document.getElementById('status-info')!;
    
    // Set initial stage class
    document.body.classList.add('stage-subatomic');
    // Set initial theme
    this.applyUniverseColorScheme(this.state.stats.universeCycles);
    
    // Set initial era title
    this.state.updateGameStage();
    this.elements.eraTitle.textContent = this.getEraTitle(this.state.currentStage);
    
    // Show dev mode indicator
    if (this.state.DEV_MODE) {
      const devIndicator = document.getElementById('dev-mode-indicator');
      if (devIndicator) {
        devIndicator.style.display = 'block';
      }
    }
    
    this.init();
  }

  /**
   * Initialize the game
   */
  private init(): void {
    // Try to load saved game
    const loaded = this.state.load();
    if (loaded) {
      this.addLogEntry('💾 Save data loaded. Welcome back, architect of reality.');
      this.addLogEntry('Your previous work persists. The atoms remember you.');
    } else {
      this.addLogEntry('═══════════════════════════════════════');
      this.addLogEntry('🌌 ELEMENT ZERO LABORATORY INITIALIZED');
      this.addLogEntry('═══════════════════════════════════════');
      if (this.state.DEV_MODE) {
        this.addLogEntry('🚀 DEVELOPER MODE ACTIVE');
        this.addLogEntry(`⚡ Production: ${this.state.DEV_SPEED_MULTIPLIER}x faster | Costs: ÷${this.state.DEV_COST_DIVISOR}`);
        this.addLogEntry('───────────────────────────────────────');
      }
      this.addLogEntry('In the beginning, there was nothing. Not even nothing.');
      this.addLogEntry('You stand at the edge of creation itself.');
      this.addLogEntry('Click to extract the first quarks from the quantum foam...');
    }

    // Set up event listeners
    this.setupEventListeners();

    // Start game loop
    this.lastFrameTime = performance.now();
    this.gameLoop(this.lastFrameTime);

    // Auto-save every 30 seconds
    setInterval(() => this.state.save(), 30000);

    // Initial UI update
    this.updateUI();
  }

  /**
   * Set up all event listeners
   */
  private setupEventListeners(): void {
    // Quark clicker
    this.elements.quarkClicker.addEventListener('click', () => {
      this.state.clickQuark();
      this.createClickEffect();
      
      // Check if combo just activated
      const comboStatus = this.state.getComboStatus();
      if (comboStatus.isActive && comboStatus.count === this.state.COMBO_THRESHOLD) {
        this.addLogEntry('⚡ Quantum Fluctuation Multiplier activated! 2x click power for 5 seconds!');
      }
      
      this.updateUI();
    });

    // Quantum Fluctuation upgrade
    this.elements.quantumBuy.addEventListener('click', (e) => {
      const prevLevel = this.state.upgrades.get('quantum-fluctuation')?.level || 0;
      if (this.state.purchaseUpgrade('quantum-fluctuation')) {
        // Quantum ripple effect
        this.createUpgradeEffect(this.elements.quantumCard, 'quantumRipple');
        this.createParticle(e.clientX, e.clientY, '⚛️', '#58A6FF');
        this.triggerCorePulse();
        
        if (prevLevel === 0) {
          this.addLogEntry('💫 The quantum vacuum trembles. Reality itself begins to yield quarks from the void...');
        } else if (prevLevel === 5) {
          this.addLogEntry('⚛️ Quantum fluctuations intensify. The fabric of spacetime ripples with possibility.');
        } else if (prevLevel === 10) {
          this.addLogEntry('🌀 The quantum field resonates at unprecedented levels. Matter flows like water.');
        } else {
          this.addLogEntry(`Quantum Fluctuation upgraded to level ${prevLevel + 1}`);
        }
        this.updateUI();
      }
    });

    // Strong Interaction upgrade
    this.elements.strongBuy.addEventListener('click', (e) => {
      const prevLevel = this.state.upgrades.get('strong-interaction')?.level || 0;
      if (this.state.purchaseUpgrade('strong-interaction')) {
        // Nuclear burst effect
        this.createUpgradeEffect(this.elements.strongCard, 'nuclearBurst');
        this.createParticle(e.clientX, e.clientY, '⚡', '#FFD700');
        this.triggerCorePulse();
        
        if (prevLevel === 0) {
          this.addLogEntry('⚡ The strong nuclear force awakens! Quarks dance and bind, forming the seeds of matter.');
        } else if (prevLevel === 5) {
          this.addLogEntry('🔗 Nuclear bonds strengthen. The architecture of atoms becomes inevitable.');
        } else {
          this.addLogEntry(`Strong Interaction upgraded to level ${prevLevel + 1}`);
        }
        this.updateUI();
      }
    });

    // Proton Synthesizer
    this.elements.protonBuy.addEventListener('click', (e) => {
      const totalProtons = Math.floor(this.state.resources.protons);
      if (this.state.purchaseUpgrade('proton-synthesizer')) {
        // Proton binding effect
        this.createUpgradeEffect(this.elements.protonCard, 'protonBind');
        this.createParticle(e.clientX, e.clientY, '🔵', '#58A6FF');
        this.triggerCorePulse();
        
        if (totalProtons === 0) {
          this.addLogEntry('🔵 First proton synthesized! The building blocks of atomic nuclei emerge.');
        } else if (totalProtons === 10) {
          this.addLogEntry('⚛️ Ten protons achieved. Your laboratory hums with nuclear potential.');
        } else {
          this.addLogEntry(`Proton synthesized! Total: ${totalProtons + 1}`);
        }
        this.updateUI();
      }
    });

    // Neutron Generator
    this.elements.neutronBuy.addEventListener('click', (e) => {
      const prevLevel = this.state.upgrades.get('neutron-generator')?.level || 0;
      if (this.state.purchaseUpgrade('neutron-generator')) {
        // Neutron pulse effect
        this.createUpgradeEffect(this.elements.neutronCard, 'neutronPulse');
        this.createParticle(e.clientX, e.clientY, '⚪', '#C8C8C8');
        this.triggerCorePulse();
        
        if (prevLevel === 0) {
          this.addLogEntry('⚪ Neutron generator activated! The missing piece of atomic nuclei emerges.');
        }
        this.updateUI();
      }
    });

    // Hydrogen Atom
    this.elements.hydrogenBuy.addEventListener('click', () => {
      const prevCount = Math.floor(this.state.resources.hydrogen);
      if (this.state.purchaseUpgrade('hydrogen-atom')) {
        // Hydrogen shimmer effect
        this.createElementEffect(this.elements.hydrogenCard, 'hydrogen');
        this.createFlyingElement(this.elements.hydrogenBuy, '💧', '#58A6FF');
        
        if (prevCount === 0) {
          this.addLogEntry('🌟 BREAKTHROUGH! First hydrogen atom created! You have touched the very foundation of stars.');
        }
        this.updateUI();
      }
    });

    // Helium Atom
    this.elements.heliumBuy.addEventListener('click', () => {
      const prevCount = Math.floor(this.state.resources.helium);
      if (this.state.purchaseUpgrade('helium-atom')) {
        // Helium float effect
        this.createElementEffect(this.elements.heliumCard, 'helium');
        this.createFlyingElement(this.elements.heliumBuy, '🎈', '#FFC0CB');
        
        if (prevCount === 0) {
          this.addLogEntry('🎈 Helium synthesized! The second element. Hydrogen production doubles!');
        }
        this.updateUI();
      }
    });

    // Carbon Atom
    this.elements.carbonBuy.addEventListener('click', () => {
      const prevCount = Math.floor(this.state.resources.carbon);
      if (this.state.purchaseUpgrade('carbon-atom')) {
        // Carbon sparkle effect
        this.createElementEffect(this.elements.carbonCard, 'carbon');
        this.createFlyingElement(this.elements.carbonBuy, '💎', '#FFFFFF');
        
        if (prevCount === 0) {
          this.addLogEntry('💎 Carbon created! The foundation of organic chemistry. Life becomes possible.');
        }
        this.updateUI();
      }
    });

    // Nitrogen Atom
    this.elements.nitrogenBuy.addEventListener('click', () => {
      const prevCount = Math.floor(this.state.resources.nitrogen);
      if (this.state.purchaseUpgrade('nitrogen-atom')) {
        // Nitrogen growth effect
        this.createElementEffect(this.elements.nitrogenCard, 'nitrogen');
        this.createFlyingElement(this.elements.nitrogenBuy, '🌿', '#228B22');
        
        if (prevCount === 0) {
          this.addLogEntry('🌿 Nitrogen synthesized! Essential for life. Quark production surges!');
        }
        this.updateUI();
      }
    });

    // Oxygen Atom
    this.elements.oxygenBuy.addEventListener('click', () => {
      const prevCount = Math.floor(this.state.resources.oxygen);
      if (this.state.purchaseUpgrade('oxygen-atom')) {
        // Oxygen breathe effect
        this.createElementEffect(this.elements.oxygenCard, 'oxygen');
        this.createFlyingElement(this.elements.oxygenBuy, '💨', '#87CEFA');
        
        if (prevCount === 0) {
          this.addLogEntry('💨 Oxygen created! The breath of existence. Energy production amplified!');
        }
        this.updateUI();
      }
    });

    // Iron Atom
    this.elements.ironBuy.addEventListener('click', () => {
      const prevCount = Math.floor(this.state.resources.iron);
      if (this.state.purchaseUpgrade('iron-atom')) {
        // Iron magnetic effect
        this.createElementEffect(this.elements.ironCard, 'iron');
        this.createFlyingElement(this.elements.ironBuy, '🔴', '#FF7B72');
        
        if (prevCount === 0) {
          this.addLogEntry('🔴 IRON SYNTHESIZED! The stellar endpoint. Beyond this lies only collapse...');
          this.addLogEntry('⚠️ WARNING: Creating 10 iron atoms will trigger a supernova!');
        } else if (prevCount + 1 >= 5) {
          this.addLogEntry(`⚠️ ${prevCount + 1} iron atoms. Reality strains under the weight...`);
        }
        this.updateUI();
      }
    });


    // Permanent Upgrades
    this.elements.permClickBuy.addEventListener('click', (e) => {
      if (this.state.purchasePermanentUpgrade('click-power')) {
        const card = document.getElementById('perm-click-card');
        if (card) this.createUpgradeEffect(card, 'elementGlow');
        this.createParticle(e.clientX, e.clientY, '⭐', '#FFD700');
        this.addLogEntry('⭐ Enhanced Click Power upgraded! Permanent power flows through your fingers.');
        this.updateUI();
      }
    });

    this.elements.permProductionBuy.addEventListener('click', (e) => {
      if (this.state.purchasePermanentUpgrade('production-boost')) {
        const card = document.getElementById('perm-production-card');
        if (card) this.createUpgradeEffect(card, 'elementGlow');
        this.createParticle(e.clientX, e.clientY, '⭐', '#FFD700');
        this.addLogEntry('⭐ Production Amplifier upgraded! The universe bends to your will.');
        this.updateUI();
      }
    });

    this.elements.permCostBuy.addEventListener('click', (e) => {
      if (this.state.purchasePermanentUpgrade('cost-reduction')) {
        const card = document.getElementById('perm-cost-card');
        if (card) this.createUpgradeEffect(card, 'elementGlow');
        this.createParticle(e.clientX, e.clientY, '⭐', '#FFD700');
        this.addLogEntry('⭐ Cost Efficiency upgraded! Resources flow more freely.');
        this.updateUI();
      }
    });

    this.elements.permAutoBuy.addEventListener('click', (e) => {
      if (this.state.purchasePermanentUpgrade('auto-clicker')) {
        const card = document.getElementById('perm-auto-card');
        if (card) this.createUpgradeEffect(card, 'elementGlow');
        this.createParticle(e.clientX, e.clientY, '⭐', '#FFD700');
        this.addLogEntry('⭐ Auto-Clicker upgraded! Reality clicks itself into existence.');
        this.updateUI();
      }
    });

    this.elements.permStartingBuy.addEventListener('click', (e) => {
      if (this.state.purchasePermanentUpgrade('starting-quarks')) {
        const card = document.getElementById('perm-starting-card');
        if (card) this.createUpgradeEffect(card, 'elementGlow');
        this.createParticle(e.clientX, e.clientY, '⭐', '#FFD700');
        this.addLogEntry('⭐ Quantum Reservoir upgraded! Each rebirth begins with more power.');
        this.updateUI();
      }
    });

    // Hard Reset Button
    this.elements.hardResetButton.addEventListener('click', () => {
      this.showHardResetModal();
    });

    // Hard Reset Modal - Confirm
    this.elements.confirmHardReset.addEventListener('click', () => {
      this.performHardReset();
    });

    // Hard Reset Modal - Cancel
    this.elements.cancelHardReset.addEventListener('click', () => {
      this.hideHardResetModal();
    });

    // Close modal on overlay click
    this.elements.hardResetModal.addEventListener('click', (e) => {
      if (e.target === this.elements.hardResetModal) {
        this.hideHardResetModal();
      }
    });

    // Save on page unload
    window.addEventListener('beforeunload', () => {
      this.state.save();
    });

    // Mobile Tab Navigation
    this.setupMobileTabs();

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        this.state.clickQuark();
        this.createClickEffect();
        
        // Check if combo just activated
        const comboStatus = this.state.getComboStatus();
        if (comboStatus.isActive && comboStatus.count === this.state.COMBO_THRESHOLD) {
          this.addLogEntry('⚡ Quantum Fluctuation Multiplier activated! 2x click power for 5 seconds!');
        }
        
        this.updateUI();
      }
    });

    // Energy Accelerator buttons
    this.setupEnergyAccelerator();
  }

  /**
   * Setup energy accelerator button handlers
   */
  private setupEnergyAccelerator(): void {
    const levels: EnergyBoostLevel[] = [0, 1, 2, 3, 4];
    
    levels.forEach(level => {
      const button = this.elements.boostButtons[level];
      if (button) {
        button.addEventListener('click', () => {
          const prevLevel = this.state.energyBoost.level;
          
          if (level === 0) {
            // Turn off
            this.state.setEnergyBoostLevel(0);
            if (prevLevel > 0) {
              this.addLogEntry('⚡ Energy Accelerator deactivated.');
            }
          } else if (this.state.canAffordEnergyBoost(level)) {
            this.state.setEnergyBoostLevel(level);
            const config = ENERGY_BOOST_CONFIG[level];
            if (prevLevel !== level) {
              this.addLogEntry(`⚡ Energy Accelerator set to ${config.name}! Production x${config.multiplier}, consuming ${config.energyPerSecond}⚡/s`);
            }
          }
          
          this.updateUI();
        });
      }
    });
  }

  /**
   * Main game loop using requestAnimationFrame
   */
  private gameLoop(currentTime: number): void {
    const deltaTime = currentTime - this.lastFrameTime;
    this.lastFrameTime = currentTime;

    // Update game state
    this.state.update(deltaTime);

    // Update UI
    this.updateUI();

    // Continue loop
    this.animationFrameId = requestAnimationFrame((time) => this.gameLoop(time));
  }

  /**
   * Update all UI elements
   */
  private updateUI(): void {
    // Update resource displays
    this.updateResourceDisplay(
      this.elements.quarksDisplay,
      this.state.resources.quarks
    );
    this.updateResourceDisplay(
      this.elements.protonsDisplay,
      this.state.resources.protons
    );
    this.updateResourceDisplay(
      this.elements.neutronsDisplay,
      this.state.resources.neutrons
    );
    this.updateResourceDisplay(
      this.elements.energyDisplay,
      this.state.resources.energy
    );
    this.updateResourceDisplay(
      this.elements.neutronStarDisplay,
      this.state.resources.neutronStarMatter
    );

    // Update click power
    const clickPower = this.state.getClickPower();
    this.elements.clickPower.textContent = `+${formatNumber(clickPower)} Quark${clickPower !== 1 ? 's' : ''} per click`;

    // Update combo indicator
    const comboStatus = this.state.getComboStatus();
    if (comboStatus.isActive) {
      this.elements.comboIndicator.textContent = `⚡ QUANTUM BOOST ACTIVE! (${Math.ceil((this.state.clickCombo.expiresAt - Date.now()) / 1000)}s)`;
      this.elements.comboIndicator.classList.add('active');
      this.elements.comboProgress.style.width = '100%';
    } else if (comboStatus.count > 0) {
      this.elements.comboIndicator.textContent = `Combo: ${comboStatus.count}/${this.state.COMBO_THRESHOLD}`;
      this.elements.comboIndicator.classList.remove('active');
      this.elements.comboProgress.style.width = `${comboStatus.progress * 100}%`;
    } else {
      this.elements.comboIndicator.textContent = `Click rapidly to activate Quantum Boost!`;
      this.elements.comboIndicator.classList.remove('active');
      this.elements.comboProgress.style.width = '0%';
    }

    // Update Quantum Fluctuation
    const quantum = this.state.upgrades.get('quantum-fluctuation')!;
    this.elements.quantumLevel.textContent = quantum.level.toString();
    const quantumProduction = quantum.level * (1 + this.state.resources.protons * 0.1);
    this.elements.quantumProduction.textContent = formatNumber(quantumProduction);
    this.elements.quantumCost.textContent = `${formatNumber(this.state.getUpgradeCost('quantum-fluctuation'))} Quarks`;
    this.updateButton(
      this.elements.quantumBuy,
      this.state.canAffordUpgrade('quantum-fluctuation')
    );

    // Update Strong Interaction
    const strong = this.state.upgrades.get('strong-interaction')!;
    this.elements.strongLevel.textContent = strong.level.toString();
    this.elements.strongCost.textContent = `${formatNumber(this.state.getUpgradeCost('strong-interaction'))} Quarks`;
    this.updateButton(
      this.elements.strongBuy,
      this.state.canAffordUpgrade('strong-interaction')
    );

    // Update Proton Synthesizer
    const protonSynth = this.state.upgrades.get('proton-synthesizer')!;
    this.elements.protonLevel.textContent = Math.floor(this.state.resources.protons).toString();
    this.elements.protonCost.textContent = `${this.state.QUARKS_PER_PROTON} Quarks`;
    
    if (protonSynth.unlocked) {
      this.elements.protonCard.classList.remove('locked');
      this.updateButton(
        this.elements.protonBuy,
        this.state.canAffordUpgrade('proton-synthesizer')
      );
    } else {
      this.elements.protonCard.classList.add('locked');
    }

    // Update Neutron Generator
    const neutron = this.state.upgrades.get('neutron-generator')!;
    this.elements.neutronLevel.textContent = neutron.level.toString();
    const neutronProduction = neutron.level * 0.1;
    this.elements.neutronProduction.textContent = formatNumber(neutronProduction);
    this.elements.neutronCost.textContent = `${formatNumber(this.state.getUpgradeCost('neutron-generator'))} Quarks`;
    
    if (neutron.unlocked) {
      this.elements.neutronCard.classList.remove('locked');
      this.updateButton(this.elements.neutronBuy, this.state.canAffordUpgrade('neutron-generator'));
    } else {
      this.elements.neutronCard.classList.add('locked');
    }

    // Update Elements
    this.updateElementCard('hydrogen', this.elements.hydrogenLevel, this.elements.hydrogenCost, 
      this.elements.hydrogenBuy, this.elements.hydrogenCard);
    this.updateElementCard('helium', this.elements.heliumLevel, this.elements.heliumCost, 
      this.elements.heliumBuy, this.elements.heliumCard);
    this.updateElementCard('carbon', this.elements.carbonLevel, this.elements.carbonCost, 
      this.elements.carbonBuy, this.elements.carbonCard);
    this.updateElementCard('nitrogen', this.elements.nitrogenLevel, this.elements.nitrogenCost, 
      this.elements.nitrogenBuy, this.elements.nitrogenCard);
    this.updateElementCard('oxygen', this.elements.oxygenLevel, this.elements.oxygenCost, 
      this.elements.oxygenBuy, this.elements.oxygenCard);
    this.updateElementCard('iron', this.elements.ironLevel, this.elements.ironCost, 
      this.elements.ironBuy, this.elements.ironCard);

    // Update Permanent Upgrades
    this.updatePermanentUpgrades();

    // Update stats
    this.elements.universeCycle.textContent = this.state.stats.universeCycles.toString();
    this.elements.totalQuarks.textContent = formatNumber(this.state.stats.totalQuarksCollected);
    this.elements.quarksPerSec.textContent = formatNumber(this.state.production.quarksPerSecond);
    this.elements.energyPerSec.textContent = formatNumber(this.state.production.energyPerSecond);

    // Show/hide permanent upgrades section
    if (this.state.stats.universeCycles > 0 || this.state.resources.neutronStarMatter > 0) {
      this.elements.permanentSection.style.display = 'block';
    }

    // Update energy accelerator UI
    this.updateEnergyAccelerator();

    // Update visual stage
    this.updateVisualStage();
    
    // Check for milestones
    this.checkMilestones();
    
    // Update click area feedback
    this.updateClickAreaFeedback();
    
    // Update universe end sequence
    this.updateUniverseEndUI();
  }

  /**
   * Update an element card
   */
  private updateElementCard(
    elementId: keyof typeof this.state.ELEMENTS,
    levelElement: HTMLElement,
    costElement: HTMLElement,
    buyButton: HTMLElement,
    cardElement: HTMLElement
  ): void {
    const upgrade = this.state.upgrades.get(`${elementId}-atom`)!;
    const count = Math.floor(this.state.resources[elementId]);
    const recipe = this.state.ELEMENTS[elementId];
    
    levelElement.textContent = count.toString();
    costElement.textContent = `${recipe.protons}p + ${recipe.neutrons}n`;
    
    if (upgrade.unlocked) {
      cardElement.classList.remove('locked');
      this.updateButton(buyButton, this.state.canAffordUpgrade(`${elementId}-atom`));
    } else {
      cardElement.classList.add('locked');
    }
  }

  /**
   * Update energy accelerator UI
   */
  private updateEnergyAccelerator(): void {
    // Show accelerator when player has hydrogen (starts generating energy)
    if (this.state.resources.hydrogen >= 1 || this.state.resources.energy > 0) {
      this.elements.energyAcceleratorSection.style.display = 'block';
    } else {
      this.elements.energyAcceleratorSection.style.display = 'none';
      return;
    }

    const currentLevel = this.state.energyBoost.level;
    const isActive = this.state.energyBoost.isActive;
    
    // Update container active state
    if (isActive && currentLevel > 0) {
      this.elements.energyAcceleratorSection.classList.add('active');
    } else {
      this.elements.energyAcceleratorSection.classList.remove('active');
    }

    // Update status text
    if (currentLevel === 0) {
      this.elements.acceleratorStatus.textContent = 'OFF';
      this.elements.acceleratorStatus.classList.remove('active');
    } else {
      const config = ENERGY_BOOST_CONFIG[currentLevel];
      this.elements.acceleratorStatus.textContent = config.name.toUpperCase();
      this.elements.acceleratorStatus.classList.add('active');
      this.elements.acceleratorStatus.style.color = config.color;
    }

    // Update buttons
    const levels: EnergyBoostLevel[] = [0, 1, 2, 3, 4];
    levels.forEach(level => {
      const button = this.elements.boostButtons[level];
      if (button) {
        // Set active state
        if (level === currentLevel) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }

        // Set disabled state (can't afford)
        if (level === 0 || this.state.canAffordEnergyBoost(level)) {
          button.removeAttribute('disabled');
        } else {
          button.setAttribute('disabled', 'true');
        }
      }
    });

    // Update info display
    const multiplier = this.state.getEnergyBoostMultiplier();
    const drain = this.state.getEnergyBoostConsumption();
    
    this.elements.acceleratorMultiplier.textContent = `Production: ${multiplier}x`;
    this.elements.acceleratorDrain.textContent = `Drain: ${drain}⚡/s`;

    // Update energy resource visual when boosting
    if (isActive && currentLevel > 0) {
      this.elements.energyResource.classList.add('boosting');
    } else {
      this.elements.energyResource.classList.remove('boosting');
    }
  }

  /**
   * Update permanent upgrades UI
   */
  private updatePermanentUpgrades(): void {
    // Click Power
    const clickPower = this.state.permanentUpgrades.get('click-power')!;
    this.elements.permClickLevel.textContent = clickPower.level.toString();
    this.elements.permClickCost.textContent = `${formatNumber(this.state.getPermanentUpgradeCost('click-power'))} ⭐`;
    this.updateButton(this.elements.permClickBuy, this.state.canAffordPermanentUpgrade('click-power'));

    // Production Boost
    const productionBoost = this.state.permanentUpgrades.get('production-boost')!;
    this.elements.permProductionLevel.textContent = productionBoost.level.toString();
    this.elements.permProductionCost.textContent = `${formatNumber(this.state.getPermanentUpgradeCost('production-boost'))} ⭐`;
    this.updateButton(this.elements.permProductionBuy, this.state.canAffordPermanentUpgrade('production-boost'));

    // Cost Reduction
    const costReduction = this.state.permanentUpgrades.get('cost-reduction')!;
    this.elements.permCostLevel.textContent = costReduction.level.toString();
    this.elements.permCostCost.textContent = `${formatNumber(this.state.getPermanentUpgradeCost('cost-reduction'))} ⭐`;
    this.updateButton(this.elements.permCostBuy, this.state.canAffordPermanentUpgrade('cost-reduction'));

    // Auto Clicker
    const autoClicker = this.state.permanentUpgrades.get('auto-clicker')!;
    this.elements.permAutoLevel.textContent = autoClicker.level.toString();
    this.elements.permAutoCost.textContent = `${formatNumber(this.state.getPermanentUpgradeCost('auto-clicker'))} ⭐`;
    this.updateButton(this.elements.permAutoBuy, this.state.canAffordPermanentUpgrade('auto-clicker'));

    // Starting Quarks
    const startingQuarks = this.state.permanentUpgrades.get('starting-quarks')!;
    this.elements.permStartingLevel.textContent = startingQuarks.level.toString();
    this.elements.permStartingCost.textContent = `${formatNumber(this.state.getPermanentUpgradeCost('starting-quarks'))} ⭐`;
    this.updateButton(this.elements.permStartingBuy, this.state.canAffordPermanentUpgrade('starting-quarks'));
  }

  /**
   * Check and announce milestones
   */
  private checkMilestones(): void {
    const stats = this.state.stats;
    const resources = this.state.resources;
    
    // Click milestones
    if (stats.totalClicks >= 100 && !this.achievedMilestones.has('clicks_100')) {
      this.achievedMilestones.add('clicks_100');
      this.addLogEntry('🏆 MILESTONE: 100 clicks! Your fingers have touched reality 100 times.');
    }
    
    if (stats.totalClicks >= 1000 && !this.achievedMilestones.has('clicks_1000')) {
      this.achievedMilestones.add('clicks_1000');
      this.addLogEntry('🏆 MILESTONE: 1,000 clicks! Dedication incarnate. The universe takes notice.');
    }
    
    // Quark milestones
    if (stats.totalQuarksCollected >= 1000 && !this.achievedMilestones.has('quarks_1k')) {
      this.achievedMilestones.add('quarks_1k');
      this.addLogEntry('🏆 MILESTONE: 1,000 quarks collected! The quantum realm bends to your will.');
    }
    
    if (stats.totalQuarksCollected >= 1000000 && !this.achievedMilestones.has('quarks_1m')) {
      this.achievedMilestones.add('quarks_1m');
      this.addLogEntry('🏆 MILESTONE: 1 MILLION quarks! You command the building blocks of existence.');
    }
    
    // Energy milestones
    if (resources.energy >= 100 && !this.achievedMilestones.has('energy_100')) {
      this.achievedMilestones.add('energy_100');
      this.addLogEntry('🏆 MILESTONE: 100 energy! Power flows through your laboratory like lightning.');
    }
    
    // Hydrogen milestones
    if (resources.hydrogen >= 50 && !this.achievedMilestones.has('hydrogen_50')) {
      this.achievedMilestones.add('hydrogen_50');
      this.addLogEntry('🏆 MILESTONE: 50 hydrogen atoms! You have created a miniature star nursery.');
    }
  }

  /**
   * Get era title based on game stage
   */
  private getEraTitle(stage: string): string {
    const titles: Record<string, string> = {
      'subatomic': 'Subatomic Era',
      'atomic': 'Atomic Era',
      'fusion': 'Fusion Era',
      'supernova': 'Supernova Era'
    };
    return titles[stage] || 'Subatomic Era';
  }

  /**
   * Update visual stage based on game progress
   */
  private updateVisualStage(): void {
    const newStage = this.state.currentStage;
    
    if (newStage !== this.currentVisualStage) {
      // Remove old stage class
      document.body.classList.remove(`stage-${this.currentVisualStage}`);
      
      // Add new stage class
      document.body.classList.add(`stage-${newStage}`);
      
      // Update era title
      this.elements.eraTitle.textContent = this.getEraTitle(newStage);
      
      // Log stage transition
      if (newStage === 'atomic') {
        this.addLogEntry('🌌 Entering Atomic Era... The universe begins to take form.');
      } else if (newStage === 'fusion') {
        this.addLogEntry('🔥 Fusion Era begins! The power of stars flows through your laboratory.');
      } else if (newStage === 'supernova') {
        this.addLogEntry('💥 SUPERNOVA STAGE! Reality trembles. The end approaches...');
        this.addLogEntry('⚠️ Continue creating hydrogen atoms to trigger the Universe End.');
      }
      
      this.currentVisualStage = newStage;
    } else {
      // Even if stage hasn't changed, ensure title is correct
      this.elements.eraTitle.textContent = this.getEraTitle(newStage);
    }
  }

  /**
   * Update a resource display with smooth animation
   */
  private updateResourceDisplay(element: HTMLElement, value: number): void {
    const elementId = element.id;
    
    // Initialize displayed value if not exists
    if (!this.displayedValues.has(elementId)) {
      this.displayedValues.set(elementId, value);
    }
    
    const currentDisplayed = this.displayedValues.get(elementId)!;
    
    // Lerp towards target value for smooth animation
    let newDisplayed: number;
    if (Math.abs(value - currentDisplayed) < 0.01) {
      newDisplayed = value; // Snap to final value if very close
    } else {
      newDisplayed = currentDisplayed + (value - currentDisplayed) * this.ANIMATION_SPEED;
    }
    
    this.displayedValues.set(elementId, newDisplayed);
    
    const formatted = formatNumber(newDisplayed);
    if (element.textContent !== formatted) {
      element.textContent = formatted;
      
      // Add pulse animation when value increases significantly
      if (newDisplayed > currentDisplayed * 1.1) {
        element.classList.add('updating');
        setTimeout(() => element.classList.remove('updating'), 300);
      }
    }
  }

  /**
   * Update button state (enabled/disabled)
   */
  private updateButton(button: HTMLElement, canAfford: boolean): void {
    if (canAfford) {
      button.removeAttribute('disabled');
    } else {
      button.setAttribute('disabled', 'true');
    }
  }

  /**
   * Create visual effect for clicking
   */
  private createClickEffect(): void {
    const clicker = this.elements.quarkClicker;
    clicker.style.transform = 'scale(0.95)';
    setTimeout(() => {
      clicker.style.transform = '';
    }, 100);
  }

  /**
   * Create particle effect
   */
  private createParticle(x: number, y: number, text: string, color: string): void {
    const particle = document.createElement('div');
    particle.className = 'particle';
    particle.textContent = text;
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;
    particle.style.color = color;
    
    document.body.appendChild(particle);
    
    setTimeout(() => {
      particle.remove();
    }, 1000);
  }

  /**
   * Create upgrade effect on card
   */
  private createUpgradeEffect(cardElement: HTMLElement, animationName: string): void {
    // Add animation class
    cardElement.style.animation = `${animationName} 0.6s ease-out`;
    
    // Remove animation after completion
    setTimeout(() => {
      cardElement.style.animation = '';
    }, 600);
  }

  /**
   * Create element synthesis effect
   */
  private createElementEffect(cardElement: HTMLElement, elementType: string): void {
    const animations: { [key: string]: string } = {
      'hydrogen': 'hydrogenShimmer',
      'helium': 'heliumFloat',
      'carbon': 'carbonSparkle',
      'nitrogen': 'nitrogenGrow',
      'oxygen': 'oxygenBreathe',
      'iron': 'ironMagnetic'
    };
    
    const animationName = animations[elementType] || 'elementGlow';
    cardElement.style.animation = `${animationName} 0.8s ease-out`;
    
    setTimeout(() => {
      cardElement.style.animation = '';
    }, 800);
  }

  /**
   * Update click area visual feedback
   */
  private updateClickAreaFeedback(): void {
    const stage = this.state.currentStage;
    
    // Update stage class on click section
    this.clickSection.className = `click-section stage-${stage}`;
    
    // Update stage text
    const stageNames: { [key: string]: string } = {
      'subatomic': 'Quantum Void',
      'atomic': 'Atomic Era',
      'fusion': 'Fusion Age',
      'supernova': 'Supernova Stage'
    };
    this.statusStage.textContent = stageNames[stage] || 'Unknown';
    
    // Update status based on resources
    const quarks = Math.floor(this.state.resources.quarks);
    const protons = Math.floor(this.state.resources.protons);
    const elements = Math.floor(
      this.state.resources.hydrogen + 
      this.state.resources.helium + 
      this.state.resources.carbon +
      this.state.resources.nitrogen +
      this.state.resources.oxygen +
      this.state.resources.iron
    );
    
    if (stage === 'subatomic') {
      this.statusInfo.textContent = `${quarks} Quarks | ${protons} Protons`;
    } else if (stage === 'atomic') {
      this.statusInfo.textContent = `${elements} Atoms | ${Math.floor(this.state.resources.energy)} Energy`;
    } else if (stage === 'fusion') {
      this.statusInfo.textContent = `${elements} Atoms | Fusion Active`;
    } else if (stage === 'supernova') {
      this.statusInfo.textContent = `⚠️ ${Math.floor(this.state.resources.iron)} Iron | Critical Mass`;
    }
    
    // Update particle count based on progress
    const targetParticles = Math.min(Math.floor(elements / 2) + Math.floor(protons / 10), 12);
    this.updateClickerParticles(targetParticles);
  }

  /**
   * Update clicker particles
   */
  private updateClickerParticles(targetCount: number): void {
    if (this.particleCount === targetCount) return;
    
    if (targetCount > this.particleCount) {
      // Add particles
      for (let i = this.particleCount; i < targetCount; i++) {
        const particle = document.createElement('div');
        particle.className = 'clicker-particle';
        particle.style.animationDelay = `${i * 0.2}s`;
        particle.style.animationDuration = `${3 + Math.random() * 2}s`;
        this.clickerParticles.appendChild(particle);
      }
    } else {
      // Remove particles
      while (this.clickerParticles.children.length > targetCount) {
        const lastChild = this.clickerParticles.lastChild;
        if (lastChild) {
          this.clickerParticles.removeChild(lastChild);
        }
      }
    }
    
    this.particleCount = targetCount;
  }

  /**
   * Trigger upgrade shockwave effect
   */
  private triggerUpgradeEffect(): void {
    const button = this.elements.quarkClicker;
    const core = button.querySelector('.clicker-core') as HTMLElement;
    
    // Add shockwave
    button.classList.add('upgrade-shockwave');
    setTimeout(() => {
      button.classList.remove('upgrade-shockwave');
    }, 600);
    
    // Add energy burst
    if (core) {
      core.classList.add('energy-burst');
      setTimeout(() => {
        core.classList.remove('energy-burst');
      }, 600);
    }
  }

  /**
   * Create flying element effect (synthesize to core)
   */
  private createFlyingElement(sourceElement: HTMLElement, icon: string, color: string): void {
    const flyingEl = document.createElement('div');
    flyingEl.className = 'flying-element';
    flyingEl.textContent = icon;
    flyingEl.style.color = color;
    
    // Get source position
    const sourceRect = sourceElement.getBoundingClientRect();
    const sourceX = sourceRect.left + sourceRect.width / 2;
    const sourceY = sourceRect.top + sourceRect.height / 2;
    
    // Get target position (clicker core)
    const targetRect = this.elements.quarkClicker.getBoundingClientRect();
    const targetX = targetRect.left + targetRect.width / 2;
    const targetY = targetRect.top + targetRect.height / 2;
    
    // Calculate distance
    const deltaX = targetX - sourceX;
    const deltaY = targetY - sourceY;
    
    // Set initial position
    flyingEl.style.left = `${sourceX}px`;
    flyingEl.style.top = `${sourceY}px`;
    
    // Set CSS variables for animation
    flyingEl.style.setProperty('--fly-x', `${deltaX}px`);
    flyingEl.style.setProperty('--fly-y', `${deltaY}px`);
    flyingEl.style.setProperty('--fly-x-mid', `${deltaX * 0.5}px`);
    flyingEl.style.setProperty('--fly-y-mid', `${deltaY * 0.5 - 100}px`); // Arc upward
    
    document.body.appendChild(flyingEl);
    
    // Trigger impact effect on arrival
    setTimeout(() => {
      const core = this.elements.quarkClicker.querySelector('.clicker-core') as HTMLElement;
      if (core) {
        core.classList.add('impact');
        setTimeout(() => {
          core.classList.remove('impact');
        }, 500);
      }
    }, 400);
    
    // Remove element after animation
    setTimeout(() => {
      flyingEl.remove();
    }, 800);
  }

  /**
   * Trigger action pulse on clicker (for regular upgrades)
   */
  private triggerCorePulse(): void {
    this.triggerUpgradeEffect();
  }

  /**
   * Trigger universe ending expansion
   */
  private triggerUniverseEnding(): void {
    this.clickSection.classList.add('universe-ending');
    this.statusStage.textContent = 'UNIVERSE COLLAPSE';
    this.statusInfo.textContent = 'Reality reaches its limit...';
  }

  /**
   * Add entry to event log
   */
  private addLogEntry(message: string): void {
    const now = new Date();
    const timeString = now.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    this.eventLog.unshift({ time: timeString, message });

    // Limit log size
    if (this.eventLog.length > this.MAX_LOG_ENTRIES) {
      this.eventLog.pop();
    }

    // Update log display
    this.updateEventLog();
  }

  /**
   * Update the event log UI
   */
  private updateEventLog(): void {
    const logContainer = this.elements.eventLog;
    
    // Clear existing entries
    logContainer.innerHTML = '';

    // Add entries (reversed for display)
    this.eventLog.forEach(entry => {
      const entryElement = document.createElement('div');
      entryElement.className = 'log-entry';
      
      const timeElement = document.createElement('span');
      timeElement.className = 'log-time';
      timeElement.textContent = entry.time;
      
      const messageElement = document.createElement('span');
      messageElement.className = 'log-message';
      messageElement.textContent = entry.message;
      
      entryElement.appendChild(timeElement);
      entryElement.appendChild(messageElement);
      logContainer.appendChild(entryElement);
    });
  }

  /**
   * Update universe end UI sequence
   */
  private updateUniverseEndUI(): void {
    const universeEnd = this.state.universeEnd;

    if (!universeEnd.isTriggered) {
      // Check if we should show warning
      if (this.state.resources.hydrogen >= this.state.UNIVERSE_END_THRESHOLD_LEVEL * 0.8 && 
          !this.achievedMilestones.has('universe_warning')) {
        this.achievedMilestones.add('universe_warning');
        this.addLogEntry('⚠️ WARNING: Universe mass approaching critical threshold...');
        this.addLogEntry('🌌 Reality itself begins to strain under the weight of creation.');
      }
      return;
    }

    // Create overlay if not exists
    if (!this.universeEndOverlay) {
      this.createUniverseEndOverlay();
      this.addLogEntry('🌌 UNIVERSE MASS CRITICAL! The cosmos collapses into singularity...');
      
      // Trigger universe core expansion
      this.triggerUniverseEnding();
    }

    // Update overlay based on phase
    this.updateUniverseEndPhase(universeEnd.phase);
  }

  /**
   * Get random universe end quotes
   */
  private getRandomUniverseQuotes(): { opening: string; closing: string } {
    const quotes = [
      {
        opening: "From nothing we came, to nothing we return. Yet in between, we danced with quarks and built stars from dreams.",
        closing: "Every ending is a new beginning. The neutron star awaits, compressed wisdom of a collapsed universe, ready to ignite the next cycle with greater brilliance."
      },
      {
        opening: "In the silence before creation, potential was infinite. In the silence after collapse, wisdom becomes eternal.",
        closing: "What dies in fire is reborn in light. The universe remembers, and in remembering, grows stronger."
      },
      {
        opening: "We are the universe observing itself, learning to create from the void, one quark at a time.",
        closing: "Each cycle teaches what the last could not. In the heart of the neutron star, all lessons crystallize into power."
      },
      {
        opening: "Time is a circle, matter is a wave, and consciousness is the observer that collapses possibility into reality.",
        closing: "The star dies, but its core remains—denser, wiser, ready to seed the next cosmos with hard-won knowledge."
      },
      {
        opening: "From quantum foam to iron hearts, the journey of matter mirrors the journey of understanding.",
        closing: "In the crucible of stellar death, permanence is forged. What you've learned transcends this universe's end."
      },
      {
        opening: "The universe is not a place, but a process—an eternal dance of creation and destruction, learning and forgetting.",
        closing: "But some things cannot be forgotten. The neutron star remembers everything, compressed into a point of infinite potential."
      },
      {
        opening: "We built atoms from quarks, stars from atoms, meaning from chaos. This is the universe's purpose: to know itself.",
        closing: "And now, knowing more than before, we begin again—not as we were, but as we have become."
      }
    ];
    
    return quotes[Math.floor(Math.random() * quotes.length)];
  }

  /**
   * Create universe end overlay
   */
  private createUniverseEndOverlay(): void {
    const overlay = document.createElement('div');
    overlay.id = 'universe-end-overlay';
    overlay.className = 'universe-end-overlay';
    
    const quotes = this.getRandomUniverseQuotes();
    
    overlay.innerHTML = `
      <div class="universe-end-content">
        <div class="universe-end-icon">🌌</div>
        <h1 class="universe-title">The End of Everything</h1>
        <div class="universe-quote">
          "${quotes.opening}"
        </div>
        <div class="universe-message">
          Your universe has reached its natural conclusion. Iron, the final element of stellar fusion, has accumulated to critical mass. The star can no longer sustain itself against gravity's embrace.
        </div>
        <div class="universe-quote">
          "${quotes.closing}"
        </div>
        <button class="universe-rebirth-button">
          Begin New Universe
        </button>
      </div>
    `;
    document.body.appendChild(overlay);
    this.universeEndOverlay = overlay;

    // Add show class after a brief delay for backdrop blur animation
    setTimeout(() => {
      overlay.classList.add('show');
    }, 100);

    // Add rebirth button handler
    const rebirthButton = overlay.querySelector('.universe-rebirth-button') as HTMLElement;
    rebirthButton?.addEventListener('click', () => {
      this.handleUniverseRebirth();
    });
  }

  /**
   * Update universe end phase
   */
  private updateUniverseEndPhase(_phase: string): void {
    if (!this.universeEndOverlay) return;
    
    // The new modal design shows all content at once, no phase transitions needed
    // Just ensure the overlay is visible
    this.universeEndOverlay.style.display = 'flex';
  }

  /**
   * Handle universe rebirth
   */
  private handleUniverseRebirth(): void {
    const cycleNumber = this.state.stats.universeCycles + 1;
    
    // Apply color scheme based on cycle
    this.applyUniverseColorScheme(cycleNumber);

    // Reset universe
    this.state.resetUniverse();

    // Fade out and remove overlay
    if (this.universeEndOverlay) {
      this.universeEndOverlay.classList.remove('show');
      this.universeEndOverlay.style.opacity = '0';
      setTimeout(() => {
        if (this.universeEndOverlay) {
          this.universeEndOverlay.remove();
          this.universeEndOverlay = null;
        }
      }, 500);
    }

    // Reset visual stage
    this.currentVisualStage = 'subatomic';
    document.body.className = 'stage-subatomic';
    this.elements.eraTitle.textContent = this.getEraTitle('subatomic');

    // Add rebirth log
    const reward = this.state.calculateNeutronStarReward();
    this.addLogEntry('═══════════════════════════════════════');
    this.addLogEntry(`🌟 UNIVERSE CYCLE ${cycleNumber} BEGINS`);
    this.addLogEntry('═══════════════════════════════════════');
    this.addLogEntry(`⭐ Neutron Star Matter gained: +${reward}`);
    this.addLogEntry('From the ashes of the old universe, a new reality emerges.');
    this.addLogEntry('The cycle continues. The observer remembers.');

    // Update UI
    this.updateUI();
  }

  /**
   * Apply color scheme based on universe cycle
   */
  private applyUniverseColorScheme(cycle: number): void {
    // Remove old theme classes
    document.body.classList.remove('theme-default', 'theme-gold', 'theme-matrix', 'theme-crimson', 'theme-void');

    // Apply theme based on cycle
    const themes = ['theme-default', 'theme-gold', 'theme-matrix', 'theme-crimson', 'theme-void'];
    const themeIndex = cycle % themes.length;
    document.body.classList.add(themes[themeIndex]);

    // Log theme change
    const themeNames = ['Default', 'Golden Age', 'Matrix Code', 'Crimson Nebula', 'Void Walker'];
    if (cycle > 0) {
      this.addLogEntry(`🎨 Universe theme unlocked: ${themeNames[themeIndex]}`);
    }
  }

  /**
   * Setup mobile tab navigation
   */
  private setupMobileTabs(): void {
    const mobileTabsContainer = document.getElementById('mobile-tabs');
    if (!mobileTabsContainer) return;

    const tabs = mobileTabsContainer.querySelectorAll('.mobile-tab');
    const upgradesSection = document.querySelector('.upgrades-section') as HTMLElement;
    const rightPanel = document.querySelector('.right-panel') as HTMLElement;

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabName = (tab as HTMLElement).dataset.tab;
        
        // Update active tab
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        
        // Toggle content visibility
        if (tabName === 'upgrades') {
          upgradesSection?.classList.remove('hidden');
          rightPanel?.classList.remove('active');
        } else if (tabName === 'laboratory') {
          upgradesSection?.classList.add('hidden');
          rightPanel?.classList.add('active');
        }
      });
    });
  }

  /**
   * Show hard reset confirmation modal
   */
  private showHardResetModal(): void {
    this.elements.hardResetModal.style.display = 'flex';
  }

  /**
   * Hide hard reset confirmation modal
   */
  private hideHardResetModal(): void {
    this.elements.hardResetModal.style.display = 'none';
  }

  /**
   * Perform hard reset
   */
  private performHardReset(): void {
    // Hide modal
    this.hideHardResetModal();

    // Perform hard reset
    this.state.hardReset();

    // Clear event log
    this.eventLog = [];
    this.achievedMilestones.clear();

    // Remove universe end overlay if exists
    if (this.universeEndOverlay) {
      this.universeEndOverlay.remove();
      this.universeEndOverlay = null;
    }

    // Reset visual stage
    this.currentVisualStage = 'subatomic';
    document.body.className = 'stage-subatomic theme-default';
    this.elements.eraTitle.textContent = this.getEraTitle('subatomic');

    // Reset displayed values for smooth animation
    this.displayedValues.clear();

    // Add welcome message
    this.addLogEntry('═══════════════════════════════════════');
    this.addLogEntry('🌌 GAME COMPLETELY RESET');
    this.addLogEntry('═══════════════════════════════════════');
    this.addLogEntry('All progress cleared. The universe returns to zero.');
    this.addLogEntry('In the beginning, there was nothing. Not even nothing.');
    this.addLogEntry('You stand at the edge of creation itself.');
    this.addLogEntry('Click to extract the first quarks from the quantum foam...');

    // Update UI
    this.updateUI();
  }

  /**
   * Stop the game loop (for cleanup)
   */
  public stop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.state.save();
  }
}

// Initialize game when DOM is ready
let gameInstance: Game;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    gameInstance = new Game();
    // Export instance for debugging
    (window as any).game = gameInstance;
  });
} else {
  gameInstance = new Game();
  // Export instance for debugging
  (window as any).game = gameInstance;
}

